import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { io, type Socket } from 'socket.io-client';
import { Replayer, type eventWithTime } from 'rrweb';
import { MonitorPlay } from 'lucide-react';
import { OWNER_WATCH_NAMESPACE, type OwnerWatchSessionDto } from '@his/shared';
import 'rrweb/dist/style.css';
import { get, session } from '@/lib/api';
import { REALTIME_PATH } from '@/lib/realtime';
import { dateTime, humanize } from '@/lib/utils';
import { PageHeader } from '@/components/ui/layout';
import { Alert, Badge, Button } from '@/components/ui/primitives';

type LinkState = 'connecting' | 'waiting' | 'live' | 'ended' | 'refused';

export interface WatchInput {
  kind: 'click' | 'key';
  x?: number;
  y?: number;
  key?: string;
}

export function OwnerWatchPage() {
  const { tenantId = '', sessionId = '' } = useParams();
  const navigate = useNavigate();
  const stage = useRef<HTMLDivElement>(null);
  const replayer = useRef<Replayer | null>(null);
  const socket = useRef<Socket | null>(null);
  const [state, setState] = useState<LinkState>('connecting');
  const [control, setControl] = useState(false);
  const [controlAsked, setControlAsked] = useState(false);
  const [reason, setReason] = useState<string | null>(null);

  const details = useQuery({
    queryKey: ['owner-watch-session', tenantId, sessionId],
    queryFn: () => get<OwnerWatchSessionDto>(`/platform/tenants/${tenantId}/watch/${sessionId}`),
    retry: false,
  });

  useEffect(() => {
    const root = stage.current;
    const token = session.access();
    if (!root || !token || !sessionId) return undefined;
    const player = new Replayer([], { root, liveMode: true, mouseTail: false, UNSAFE_replayCanvas: false });
    replayer.current = player;
    const link = io(OWNER_WATCH_NAMESPACE, { path: REALTIME_PATH, transports: ['websocket'], auth: { token, sessionId }, reconnectionDelay: 2_000, reconnectionDelayMax: 15_000 });
    socket.current = link;
    let started = false;
    link.on('ready', (payload: OwnerWatchSessionDto) => {
      setState('waiting');
      setControl(payload.control);
    });
    link.on('peer', (payload: { side: string; present: boolean }) => {
      if (payload.side === 'user') setState(payload.present ? 'live' : 'waiting');
    });
    link.on('batch', (events: eventWithTime[]) => {
      if (!Array.isArray(events) || events.length === 0) return;
      if (!started) {
        const snapshotAt = events.findIndex((event) => event.type === 2);
        if (snapshotAt === -1) return;
        player.startLive(events[snapshotAt]?.timestamp);
        started = true;
      }
      for (const event of events) player.addEvent(event);
      setState('live');
    });
    link.on('control', (payload: { granted: boolean }) => {
      setControl(payload.granted);
      setControlAsked(false);
    });
    link.on('ended', (payload: { reason?: string }) => {
      setState('ended');
      setReason(payload?.reason ?? null);
    });
    link.on('refused', (payload: { reason?: string }) => {
      setState('refused');
      setReason(payload?.reason ?? null);
    });
    return () => {
      link.disconnect();
      socket.current = null;
      player.pause();
      replayer.current = null;
      root.innerHTML = '';
    };
  }, [sessionId]);

  const send = (input: WatchInput) => {
    if (control) socket.current?.emit('input', input);
  };

  const onStageClick = (event: React.MouseEvent<HTMLDivElement>) => {
    const frame = replayer.current?.iframe;
    if (!frame || !control) return;
    const rect = frame.getBoundingClientRect();
    send({ kind: 'click', x: Math.round(event.clientX - rect.left), y: Math.round(event.clientY - rect.top) });
  };

  const onStageKey = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (!control) return;
    event.preventDefault();
    send({ kind: 'key', key: event.key });
  };

  const endSession = () => {
    socket.current?.emit('end');
    setState('ended');
  };

  return (
    <>
      <PageHeader
        title={details.data ? `Watching ${details.data.userName}` : 'Watch session'}
        description={details.data ? `${details.data.reason} — ends at ${dateTime(details.data.endsAt)}.` : undefined}
        actions={
          <>
            <Button variant="outline" size="sm" onClick={() => navigate(`/owner/tenants/${tenantId}?tab=users`)}>
              Back to users
            </Button>
            {state !== 'ended' && state !== 'refused' && (
              <>
                {!control && (
                  <Button variant="outline" size="sm" disabled={state !== 'live' || controlAsked} onClick={() => (setControlAsked(true), socket.current?.emit('control-request'))}>
                    {controlAsked ? 'Waiting for the user' : 'Ask to control'}
                  </Button>
                )}
                {control && (
                  <Button variant="outline" size="sm" onClick={() => socket.current?.emit('control-release')}>
                    Release control
                  </Button>
                )}
                <Button variant="destructive" size="sm" onClick={endSession}>
                  End session
                </Button>
              </>
            )}
          </>
        }
      />
      <div role="status" className="mb-3 flex flex-wrap items-center gap-2 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs font-medium text-destructive">
        <MonitorPlay className="h-3.5 w-3.5 flex-none" aria-hidden />
        {state === 'connecting' && 'Connecting to the watch room…'}
        {state === 'waiting' && "Waiting for the user's browser. They see a red banner the moment it joins."}
        {state === 'live' && (control ? 'Live — you are controlling this screen. Every click and key you send lands on their browser.' : 'Live — the user sees a red banner that you are watching.')}
        {state === 'ended' && `The session has ended${reason ? ` (${humanize(reason, 'watch_end_reason')})` : ''}.`}
        {state === 'refused' && 'The watch room refused this connection: it may have another viewer, or the session is over.'}
        {control && <Badge variant="destructive">Remote control</Badge>}
      </div>
      {details.error && <Alert variant="warning">This watch session is no longer open.</Alert>}
      <div
        ref={stage}
        tabIndex={0}
        onClick={onStageClick}
        onKeyDown={onStageKey}
        className="watch-stage min-h-[480px] overflow-auto rounded-md border bg-muted/40 outline-none focus:ring-2 focus:ring-destructive/40"
        aria-label="Watched screen"
      />
    </>
  );
}
